import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { getGymAvailability, updateGymAvailability } from '../api';

const BRAND_NAVY = '#010449';
const BRAND_BLUE = '#2458A6';
const BRAND_ORANGE = '#F28C28';

const GymAvailabilityCard = ({
  accessToken,
  initialActive = true,
  onAvailabilityChange,
}) => {
  const [active, setActive] = useState(Boolean(initialActive));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const loadAvailability = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await getGymAvailability(accessToken);
      const nextActive = Boolean(data.active);
      setActive(nextActive);
      onAvailabilityChange?.(nextActive);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, onAvailabilityChange]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  const persistAvailability = async (nextActive) => {
    setSaving(true);
    setError('');
    try {
      const data = await updateGymAvailability(nextActive, accessToken);
      const savedActive = Boolean(data.active);
      setActive(savedActive);
      onAvailabilityChange?.(savedActive);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
      setConfirmationOpen(false);
    }
  };

  const handleSwitch = (event) => {
    if (event.target.checked) {
      persistAvailability(true);
      return;
    }
    setConfirmationOpen(true);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          direction: 'ltr',
          borderRadius: 4,
          borderColor: active ? 'rgba(1,4,73,.22)' : 'rgba(242,140,40,.45)',
          boxShadow: '0 14px 38px rgba(1,4,73,.06)',
          background: active
            ? 'linear-gradient(135deg, rgba(1,4,73,.08), rgba(255,255,255,1))'
            : 'linear-gradient(135deg, rgba(242,140,40,.13), rgba(255,255,255,1))',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: 2,
                  color: active ? BRAND_NAVY : BRAND_ORANGE,
                  bgcolor: active ? '#E9EDFF' : '#FFF2E6',
                }}
              >
                <StorefrontRoundedIcon />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={800} color={BRAND_NAVY}>
                    Disponibilité de la salle
                  </Typography>
                  {!loading && (
                    <Chip
                      size="small"
                      label={active ? 'Souscriptions ouvertes' : 'Souscriptions suspendues'}
                      sx={{
                        bgcolor: active ? BRAND_NAVY : BRAND_ORANGE,
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 620 }}>
                  {active
                    ? 'Les clients peuvent souscrire aux abonnements publiés par votre salle.'
                    : 'Aucun nouvel abonnement ne peut être acheté. Les abonnements déjà actifs restent inchangés.'}
                </Typography>
              </Box>
            </Stack>

            {loading ? (
              <CircularProgress size={28} />
            ) : (
              <FormControlLabel
                sx={{ m: 0, whiteSpace: 'nowrap' }}
                label={active ? 'Ouverte' : 'Suspendue'}
                control={(
                  <Switch
                    checked={active}
                    disabled={saving || !accessToken}
                    onChange={handleSwitch}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_NAVY },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: BRAND_BLUE },
                    }}
                    inputProps={{ 'aria-label': 'Accepter les nouvelles souscriptions' }}
                  />
                )}
              />
            )}
          </Stack>

          {error && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={loadAvailability}>Réessayer</Button>}
              sx={{ mt: 2 }}
            >
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmationOpen} onClose={() => !saving && setConfirmationOpen(false)}>
        <DialogTitle>Suspendre les nouvelles souscriptions ?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Les clients ne pourront plus acheter vos abonnements tant que vous n'aurez pas rouvert les souscriptions.
            Cette action ne désactive pas votre compte.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setConfirmationOpen(false)}>
            Annuler
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => persistAvailability(false)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: BRAND_ORANGE,
              '&:hover': { bgcolor: '#D9791E' },
            }}
          >
            Confirmer la suspension
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GymAvailabilityCard;
